import { useState } from 'react';
import { Shield, RefreshCw, Key } from 'lucide-react';
import { API_URL } from '../../utils/config';

const methodLabel = (method) => {
  switch (method) {
    case 'totp':  return 'authenticator app';
    case 'sms':   return 'phone via SMS';
    case 'email': return 'email';
    default:      return 'your device';
  }
};

const secondaryDestinationMessage = (method, mask) => {
  if (method === 'totp')  return 'Enter the 6-digit code from your backup authenticator app';
  if (method === 'sms')   return `Enter the code we sent to ${mask || 'your phone'}`;
  if (method === 'email') return `Enter the code we sent to ${mask || 'your email'}`;
  return 'Enter the code';
};

const TwoFactorVerification = ({
  tempToken,
  otpMethod,
  secondaryMethod,
  secondaryDestinationMask,
  deviceTrustEnabled = false,
  deviceTrustDays = null,
  onSuccess,
  onBack
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [showTryAnother, setShowTryAnother] = useState(false);
  const [activeRole, setActiveRole] = useState('primary'); // 'primary' | 'secondary'
  const [sendingAlternate, setSendingAlternate] = useState(false);
  // Defaults on: the whole point of the option is to stop asking a device that
  // has already proven itself. Anyone on a shared or public machine unticks it.
  const [trustDevice, setTrustDevice] = useState(true);

  const activeMethod = activeRole === 'secondary' ? secondaryMethod : otpMethod;

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = showBackupCode ? 'verify_backup_code' : 'verify';
      // We send the channel name ('totp'/'sms'/'email') and let the backend
      // determine which slot (primary/secondary) that channel occupies.
      const wantsTrust = deviceTrustEnabled && trustDevice;
      const payload = showBackupCode
        ? { temp_token: tempToken, backup_code: backupCode, trust_device: wantsTrust }
        : { temp_token: tempToken, otp_code: code, method: activeMethod, trust_device: wantsTrust };

      const response = await fetch(`${API_URL}/two_factor/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess(data);
      } else if (data.session_expired) {
        // Token lapsed — send them back to sign in for a fresh session.
        onBack(data.error || 'Please sign in again to continue.');
      } else {
        setError(data.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (activeMethod === 'totp') {
      setError('TOTP codes cannot be resent. Use your authenticator app.');
      return;
    }
    setResending(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/two_factor/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_token: tempToken, method: activeMethod })
      });
      const data = await response.json();
      if (response.ok) {
        const msg = data.message || 'Code sent successfully';
        setError(msg);
        setTimeout(() => setError(''), 3000);
      } else if (data.session_expired) {
        onBack(data.error || 'Please sign in again to continue.');
      } else {
        setError(data.error || 'Failed to resend code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleSwitchToSecondary = async () => {
    setError('');
    setSendingAlternate(true);
    try {
      // Always call send_alternate — for sms/email it sends the code, for totp
      // it returns an info payload; in every case it refreshes the temp_token so
      // the secondary verify runs against a live token.
      const response = await fetch(`${API_URL}/two_factor/send_alternate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_token: tempToken })
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.session_expired) {
          onBack(data.error || 'Please sign in again to continue.');
          return;
        }
        setError(data.error || 'Failed to send backup code');
        return;
      }
      setActiveRole('secondary');
      setShowTryAnother(false);
      setCode('');
      setShowBackupCode(false);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSendingAlternate(false);
    }
  };

  const handleSwitchToPrimary = () => {
    setActiveRole('primary');
    setShowTryAnother(false);
    setCode('');
    setError('');
    setShowBackupCode(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            Two-Factor Authentication
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {showBackupCode
              ? 'Enter one of your backup codes'
              : activeRole === 'secondary'
                ? secondaryDestinationMessage(secondaryMethod, secondaryDestinationMask)
                : `Enter the 6-digit code from your ${methodLabel(otpMethod)}`}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleVerify}>
          <div>
            <label htmlFor="code" className="sr-only">
              Verification Code
            </label>
            <input
              id="code"
              name="code"
              type="text"
              autoComplete="off"
              required
              maxLength={showBackupCode ? 8 : 6}
              className="appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest bg-white dark:bg-gray-800"
              placeholder={showBackupCode ? '••••••••' : '••••••'}
              value={showBackupCode ? backupCode : code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, showBackupCode ? 8 : 6);
                if (showBackupCode) {
                  setBackupCode(value);
                } else {
                  setCode(value);
                }
              }}
            />
          </div>

          {error && (
            <div className={`rounded-lg p-4 ${
              error.includes('sent') || error.includes('Code sent')
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400'
            }`}>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {deviceTrustEnabled && (
          <label className="flex items-start gap-2 text-left mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Skip the code on this device for {deviceTrustDays} days.
              <span className="block text-xs text-gray-500 dark:text-gray-500">
                Your password is still required every time. Leave this unchecked on a shared computer.
              </span>
            </span>
          </label>
          )}

          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={loading || (showBackupCode ? backupCode.length < 8 : code.length !== 6)}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>

            {!showBackupCode && (activeMethod === 'sms' || activeMethod === 'email') && (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="flex items-center justify-center space-x-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                <span>{resending ? 'Sending...' : 'Resend Code'}</span>
              </button>
            )}

            {!showBackupCode && secondaryMethod && activeRole === 'primary' && (
              <button
                type="button"
                onClick={() => setShowTryAnother((v) => !v)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                Try another way
              </button>
            )}

            {showTryAnother && secondaryMethod && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-left space-y-2">
                <button
                  type="button"
                  onClick={handleSwitchToSecondary}
                  disabled={sendingAlternate}
                  className="w-full text-left text-sm text-gray-800 dark:text-gray-200 hover:text-blue-600 disabled:opacity-50"
                >
                  {sendingAlternate && 'Sending...'}
                  {!sendingAlternate && secondaryMethod === 'sms' &&
                    `Send code to phone ${secondaryDestinationMask || ''}`.trim()}
                  {!sendingAlternate && secondaryMethod === 'email' &&
                    `Send code to email ${secondaryDestinationMask || ''}`.trim()}
                  {!sendingAlternate && secondaryMethod === 'totp' &&
                    'Use your backup authenticator app'}
                </button>
              </div>
            )}

            {activeRole === 'secondary' && (
              <button
                type="button"
                onClick={handleSwitchToPrimary}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Use primary method instead
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setShowBackupCode(!showBackupCode);
                setCode('');
                setBackupCode('');
                setError('');
              }}
              className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <Key className="h-4 w-4" />
              <span>{showBackupCode ? 'Use verification code instead' : 'Use backup code'}</span>
            </button>

            <button
              type="button"
              onClick={() => onBack()}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              ← Back to login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorVerification;
